// =============================================================================
// StaticSiteStudioPage — /strony/:websiteId. Studio migracji per domena, jedna
// kolumna pelnej szerokosci (max-w-7xl), sekcje jako karty StudioSection:
// Header → Motyw → Design AI → Menu → Zgodnosc → Domena → Historia → Zaawansowane.
// Podglad = linki pages.dev ↗ / preview ↗ w headerze (iframe usuniety 2026-09-22).
// =============================================================================

import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { TopBar } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Palette } from "lucide-react";
import { useStaticSite } from "@/hooks/useStaticSite";
import { useMigrationRun } from "@/hooks/useMigrationRun";
import { useThematicMaps } from "@/hooks/useThematicMaps";
import { StudioHeader } from "@/components/static-sites/StudioHeader";
import { StudioSection } from "@/components/static-sites/StudioSection";
import { StudioThemePicker } from "@/components/static-sites/StudioThemePicker";
import { ComplianceRow } from "@/components/static-sites/ComplianceRow";
import { DomainRow } from "@/components/static-sites/DomainRow";
import { HistoryRow } from "@/components/static-sites/HistoryRow";
import { AdvancedRow } from "@/components/static-sites/AdvancedRow";
import { DesignBriefCard } from "@/components/thematic-maps/migration/DesignBriefCard";
import { MenuCard } from "@/components/static-sites/MenuCard";

const StaticSiteStudioPage = () => {
  const { websiteId } = useParams<{ websiteId: string }>();
  const { site, redirects, loading, error, refresh, saveSettings, patchSite, addRedirect, removeRedirect } = useStaticSite({ websiteId });
  const { maps } = useThematicMaps();
  const run = useMigrationRun(site, () => void refresh());
  const mapName = useMemo(() => (site ? maps.find((m) => m.id === site.map_id)?.name || null : null), [maps, site]);
  const mapForWebsite = useMemo(() => (!site && websiteId ? maps.find((m) => m.website_id === websiteId) || null : null), [maps, site, websiteId]);
  const isBrand = site?.settings?.build_backend === "astro" && (site.settings.astro_theme || "brand") === "brand";

  return (
    <>
      <TopBar title="Strony statyczne · Studio" />
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="mx-auto w-full max-w-7xl space-y-6 px-6 py-6">
          {error && <Card className="border-destructive/40"><CardContent className="py-3 text-sm text-destructive">{error}</CardContent></Card>}
          {loading && !site && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Wczytuję…</div>}
          {!loading && !site && (
            <Card>
              <CardContent className="py-6 text-sm">
                Ta domena nie ma jeszcze migracji.{" "}
                {mapForWebsite ? (
                  <>Załóż ją w mapie <Link to={`/mapy-tematyczne/${mapForWebsite.id}`} className="underline">{mapForWebsite.name}</Link> (krok Migracja).</>
                ) : (
                  <>Przypisz domenę do mapy i załóż migrację w kroku Migracja. <Link to="/strony" className="underline">Wróć do listy</Link>.</>
                )}
              </CardContent>
            </Card>
          )}
          {site && (
            <>
              <StudioHeader site={site} run={run} mapName={mapName} />
              <StudioSection icon={Palette} title="Motyw" help="Brand i AstroPaper budują się w kontenerze Astro (ok. minuty). Własny renderer składa stronę w przeglądarce w kilka sekund. Zmiana motywu wymaga „Przenieś ponownie”.">
                <StudioThemePicker site={site} onSaveSettings={saveSettings} />
              </StudioSection>
              {isBrand && <DesignBriefCard site={site} onSaveSettings={saveSettings} />}
              <MenuCard site={site} onSaveSettings={saveSettings} onPatchSite={patchSite} />
              <ComplianceRow site={site} redirects={redirects} onAddRedirect={addRedirect} onRemoveRedirect={removeRedirect} onAnalyzed={() => void refresh()} />
              <DomainRow site={site} />
              <HistoryRow site={site} />
              <AdvancedRow site={site} onSaveSettings={saveSettings} onPatchSite={patchSite} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StaticSiteStudioPage;
